/**
 * Chart kit: the data side (MASTER_PROMPT §6.3). No DOM, no three.js;
 * unit-tested in Node. Both renderers (svg.js 2D, gl.js 3D) read the same
 * normalised model, so the 2D fallback and the 3D chart always agree.
 *
 * A chart spec:
 *   { kind, title, unit, x: [labels], series: [{ key, label, role, values }] }
 * kinds: "line" | "area" | "column" | "stacked" | "donut" | "waterfall" | "candlestick"
 *   donut       x = slice labels, one series of values
 *   waterfall   steps: [{ label, value, kind: "start" | "delta" | "total" }]
 *   candlestick series[0].values = [{ o, h, l, c }]
 * unit: "$" money, "%" percent, "" plain number.
 *
 * Colour follows meaning, the same everywhere in the game (role -> colour).
 */

const ROLE_COLOR = {
  cash: "#7ee8fa",
  revenue: "#34d399",
  cost: "#fb7185",
  profit: "#a3e635",
  debt: "#f6b26b",
  equity: "#a78bfa",
  rival: "#f472b6",
  risk: "#fbbf24",
  you: "#7ee8fa",
  neutral: "#9fb4ff",
};
const SLICE_COLORS = ["#7ee8fa", "#f472b6", "#a78bfa", "#fbbf24", "#34d399", "#f6b26b", "#9fb4ff", "#5eead4", "#fb7185", "#cbd5e1"];
const UP = "#34d399", DOWN = "#fb7185";

const KINDS = ["line", "area", "column", "stacked", "donut", "waterfall", "candlestick"];

function colorFor(series, i) {
  return series.color || ROLE_COLOR[series.role] || SLICE_COLORS[i % SLICE_COLORS.length];
}

/** Round axis ticks: about `count` steps of 1, 2, 2.5 or 5 x 10^n covering [min, max]. */
function niceTicks(min, max, count = 5) {
  if (!isFinite(min) || !isFinite(max)) return [0, 1];
  if (min === max) { const pad = Math.abs(min) || 1; min -= pad / 2; max += pad / 2; }
  const raw = (max - min) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || 10 * mag;
  const lo = Math.floor(min / step) * step, hi = Math.ceil(max / step) * step;
  const out = [];
  for (let v = lo; v <= hi + step / 2; v += step) out.push(+v.toFixed(10));
  return out;
}

/** Short value text for axes: $1.2M, 45%, 3.4K. Tooltips use the full figure. */
function shortValue(v, unit) {
  const a = Math.abs(v);
  const s = a >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : a >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${+v.toFixed(a < 10 ? 2 : 0)}`;
  return unit === "$" ? (v < 0 ? `-$${s.replace("-", "")}` : `$${s}`) : unit === "%" ? `${s}%` : s;
}

function fullValue(v, unit) {
  if (v == null || !isFinite(v)) return "—";
  if (unit === "$") return `${v < 0 && Math.round(Math.abs(v)) ? "-" : ""}${(() => { const a = Math.abs(v); return a >= 1e12 ? `$${(a / 1e12).toFixed(2)}T` : a >= 1e9 ? `$${(a / 1e9).toFixed(2)}B` : a >= 1e6 ? `$${(a / 1e6).toFixed(2)}M` : a >= 1e4 ? `$${(a / 1e3).toFixed(0)}K` : `$${Math.round(a).toLocaleString("en-US")}`; })()}`; // short money (2.7)
  if (unit === "%") return `${(+v).toFixed(1)}%`;
  return (+v).toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/**
 * Normalise a spec: fill colours, compute the value domain, and for a
 * waterfall turn steps into floating bars ({ label, from, to, value, kind }).
 */
function normalize(spec) {
  if (!KINDS.includes(spec.kind)) throw new Error(`unknown chart kind ${spec.kind}`);
  const m = { kind: spec.kind, title: spec.title || "", unit: spec.unit || "", x: [...(spec.x || [])] };
  if (spec.kind === "waterfall") {
    let run = 0;
    m.bars = (spec.steps || []).map((s) => {
      if (s.kind === "start" || s.kind === "total") {
        const bar = { label: s.label, from: 0, to: s.value, value: s.value, kind: s.kind };
        run = s.value;
        return bar;
      }
      const bar = { label: s.label, from: run, to: run + s.value, value: s.value, kind: s.value >= 0 ? "up" : "down" };
      run += s.value;
      return bar;
    });
    m.x = m.bars.map((b) => b.label);
    const ends = m.bars.flatMap((b) => [b.from, b.to]);
    m.min = Math.min(0, ...ends); m.max = Math.max(0, ...ends);
    m.series = [{ key: "steps", label: m.title || "Change", values: m.bars.map((b) => b.value), color: ROLE_COLOR.neutral }];
    return m;
  }
  m.series = (spec.series || []).map((s, i) => ({ key: s.key || `s${i}`, label: s.label || s.key || `Series ${i + 1}`, role: s.role || "neutral", values: [...s.values], color: colorFor(s, i) }));
  if (spec.kind === "donut") {
    const vals = m.series[0] ? m.series[0].values.map((v) => Math.max(0, +v || 0)) : [];
    const total = vals.reduce((a, b) => a + b, 0);
    m.slices = vals.map((v, i) => ({ label: m.x[i], value: v, share: total > 0 ? v / total : 0, color: (spec.colors && spec.colors[i]) || SLICE_COLORS[i % SLICE_COLORS.length] }));
    m.total = total;
    m.min = 0; m.max = total;
    return m;
  }
  if (spec.kind === "candlestick") {
    const v = m.series[0] ? m.series[0].values : [];
    m.min = Math.min(...v.map((c) => c.l)); m.max = Math.max(...v.map((c) => c.h));
    if (!v.length) { m.min = 0; m.max = 1; }
    return m;
  }
  let all;
  if (spec.kind === "stacked") {
    const n = m.x.length;
    const pos = Array(n).fill(0), neg = Array(n).fill(0);
    m.series.forEach((s) => s.values.forEach((v, i) => { if (v >= 0) pos[i] += v; else neg[i] += v; }));
    all = [...pos, ...neg];
  } else {
    all = m.series.flatMap((s) => s.values).filter((v) => v != null && isFinite(v));
  }
  m.min = Math.min(0, ...all); m.max = Math.max(0, ...all);
  if (!all.length) { m.min = 0; m.max = 1; }
  return m;
}

/** The rows of the accessible data table: [header, ...rows] of strings. */
function dataRows(m) {
  if (m.kind === "waterfall") return [["Step", "Change", "Running total"], ...m.bars.map((b) => [b.label, fullValue(b.value, m.unit), fullValue(b.to, m.unit)])];
  if (m.kind === "donut") return [["", "Value", "Share"], ...m.slices.map((s) => [s.label, fullValue(s.value, m.unit), `${(s.share * 100).toFixed(1)}%`])];
  if (m.kind === "candlestick") return [["", "Open", "High", "Low", "Close"], ...m.series[0].values.map((c, i) => [m.x[i], ...[c.o, c.h, c.l, c.c].map((v) => fullValue(v, m.unit))])];
  return [["", ...m.series.map((s) => s.label)], ...m.x.map((x, i) => [x, ...m.series.map((s) => fullValue(s.values[i], m.unit))])];
}

/** What a tap at index i says (tooltip), as [label, value, colour] lines. */
function inspectLines(m, i) {
  if (m.kind === "waterfall") {
    const b = m.bars[i];
    return b ? [[b.label, b.kind === "start" || b.kind === "total" ? fullValue(b.value, m.unit) : `${b.value >= 0 ? "+" : ""}${fullValue(b.value, m.unit)}`, b.kind === "down" ? DOWN : b.kind === "up" ? UP : ROLE_COLOR.neutral]] : [];
  }
  if (m.kind === "donut") {
    const s = m.slices[i];
    return s ? [[s.label, `${fullValue(s.value, m.unit)} · ${(s.share * 100).toFixed(1)}%`, s.color]] : [];
  }
  if (m.kind === "candlestick") {
    const c = m.series[0].values[i];
    return c ? [["Open", fullValue(c.o, m.unit)], ["High", fullValue(c.h, m.unit)], ["Low", fullValue(c.l, m.unit)], ["Close", fullValue(c.c, m.unit), c.c >= c.o ? UP : DOWN]] : [];
  }
  return m.series.map((s) => [s.label, fullValue(s.values[i], m.unit), s.color]);
}

/** Waterfall steps for a month's P&L: revenue, each cost, net income. */
function pnlSteps(is) {
  const steps = [{ label: "Revenue", value: is.revenue || 0, kind: "start" }];
  // short labels so every bar keeps its name on a phone (the meaning: costs of goods, R&D, sales & marketing, G&A, depreciation)
  for (const [key, label] of [["cogs", "Goods"], ["rnd", "R&D"], ["snm", "Sales & mktg"], ["gna", "G&A"], ["depreciation", "D&A"], ["interest", "Interest"], ["tax", "Tax"]]) {
    if (is[key]) steps.push({ label, value: -is[key], kind: "delta" });
  }
  steps.push({ label: "Net income", value: is.net_income != null ? is.net_income : steps.reduce((a, s) => a + s.value, 0), kind: "total" });
  return steps;
}

export { DOWN, KINDS, ROLE_COLOR, SLICE_COLORS, UP, dataRows, fullValue, inspectLines, niceTicks, normalize, pnlSteps, shortValue };
